import BaseListPresenter from "../../../base/BaseListPresenter";
import { dialog, Button } from "nq-component";
import { caseRegistry } from "./payrollForm/payrollCases/PayrollCasesPage";
import { DialogConfirmation } from "../../../components/common/Converter";
import { UpsertUseCase, aggregateUseCase } from "../../../usecases/object";
import { isRetryableError, delay } from "../../../usecases/object/retryUtils";
import {
  _hasNegativeValues,
  _hasNetBelow5k,
  _buildEmployeeMap,
  _formatDateToMonthYear,
  _getProcessItems,
  _areArraysEqual,
} from "./helper/helperFunction";
import { _getPayrollWithEmployeePipeline } from "./pipeline/PayrollListPipeline";
import keys from "./helper/keys.json";

const bulkUpsertUseCase = new UpsertUseCase();
/** Higher than FormPayroll default (~25); falls back if the connection overloads. */
const EMPLOYEE_PRINT_CONCURRENCY = [55, 35, 25];

const CLEARED_EMPLOYEE_TAX_FIELDS = () => ({
  annualTax: [],
  basicTax: [],
  earnedForThePeriod: [],
  monthlyPayCode: [],
  monthlyTax: [],
  netEarned: [],
  netOfMandatory: [],
});

class PayrollListlPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }
  init() {
    this.limit = 10;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      this.objects = this.objects.concat(objects);
      this.view.setObjects(objects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  dialogClose() {
    dialog.close();
  }

  withItem =
    (data = {}) =>
    (caseName) => {
      const caseHandler = caseRegistry[caseName];
      return typeof caseHandler === "function" ? caseHandler(data) : false;
    };

  _isBackPay(data) {
    const checkCase = this.withItem(data);

    return (
      checkCase("isBasicJoAddendum") ||
      checkCase("isBasicBackpay") ||
      checkCase("isApBackpay") ||
      checkCase("isIndividualAPBackpay") ||
      checkCase("isIndividualJoBackpay")
    );
  }

  _isBasic(data){
    const checkCase = this.withItem(data);
    return checkCase("isBasicGeneral") || checkCase("isBasicAddendum") || checkCase("isBasicBackpay");
  }

  _deletePayroll = async (data) => {
    const dataId = data.id;

    try {
      const filterPayroll = await this.findObjectUseCase.execute("payroll", {
        where: { payrollID: dataId },
      });

      const payrollTable = filterPayroll.map((item) => {
        return this.deleteObjectUseCase.execute("payroll", item.id);
      });

      await Promise.all([...payrollTable]);
    } catch (error) {
      console.error("Error deleting payroll records:", error);
    }
  };

  _onDeleteData(data) {
    DialogConfirmation(data.id, this.view.getCollectionName(), async () => {
      this.view.showSuccess("Data Successfully Deleted!");
      this.view.refetchData();
      await this._deletePayroll(data);
    });
  }

  async _onGeneratePayslip(data) {
    try {
      await this.view.showConfirmDialog(
        "Are you sure you want to generate payslips for this payroll?",
        "Generate Payslips",
        "GENERATE"
      );
    } catch {
      return;
    }

    const isBackpay = this._isBackPay(data);
    const collectionName = isBackpay ? "payroll_back_pay" : "payroll";

    const payrollData = await this.findObjectUseCase.execute(collectionName, {
      where: { payrollID: data.id },
      keys: ["employee"],
    });

    const payslipData = payrollData.map((item) => {
      const payslip = {
        payrollID: data.id,
        employeeID: item.employee.id,
        employee: { id: item.employee.id },
        payrollType: data.payrollType,
        jobType: data.jobType,
        payItem: data.payItem,
        coverFrom: data.startDate,
        coverTo: data.endDate,
      };

      return this.upsertUseCase.execute("payslip", payslip);
    });
    await Promise.all(payslipData);
    this.view.showSuccess("Payslip Successfully Generated!");
  }

  async _onCheckNegativeValues(data) {
    const isBackpay = this._isBackPay(data);
    const collectionName = isBackpay ? "payroll_back_pay" : "payroll";
    const payrollData = await aggregateUseCase().execute(
      collectionName,
      _getPayrollWithEmployeePipeline(data?.id)
    );

    const hasNegative = _hasNegativeValues(payrollData);
    const _isBasic = this._isBasic(data);
    const hasNetBelow5k = _hasNetBelow5k(payrollData);

    if (hasNegative || (_isBasic && hasNetBelow5k)) {
      this.view.showError(
        hasNegative
          ? "Cannot proceed! negative values found in payroll records."
          : "Cannot proceed! net amount below 5,000 found in payroll records."
      );
    } else {
        const employees = payrollData.map(({ employee }) => employee);
        this._confirmApproval({ data, payrollData, employees });
    }
  }

  _confirmApproval(data) {
    dialog.fire({
      html: (
        <div className="p-2">
          <div className="row " style={{ textAlign: "center" }}>
            <p>
              <i
                className="bi bi-question-circle"
                style={{ color: "orange", fontSize: "20px" }}
              ></i>{" "}
              "Upon Proceeding, the status will be changed to <b style={{ color: 'green' }}>Printed</b>."
              <div className="mt-1">
                <b></b>
              </div>
            </p>
            <p
              style={{
                fontSize: "13px",
                color: "rgba(0, 0, 0, .65)",
              }}
            >
              Please click on continue to proceed.
            </p>
            <hr />
          </div>

          <div className="w-100 d-flex justify-content-end gap-3 pe-3 pb-3">
            <Button className="btn btn-light fs-sm" onClick={this.dialogClose}>
              Cancel
            </Button>
            <Button
              className="btn btn-primary"
              onClick={() => this._handlePrintData(data)}
            >
              Mark as Printed
            </Button>
          </div>
        </div>
      ),
      footer: false,
    });
  }

  _buildEmployeeUpdates({
    payrollData,
    items,
    employeeMap,
    payrollType,
    isnotJoBasic,
    isIndividual,
    isAddendumBasic,
    onlyEarnedForThePeriod,
  }) {
    const updates = [];

    for (const item of payrollData ?? []) {
      const employeeId = item?.employee?.id;
      if (!employeeId) continue;

      const keyItem = items[employeeId] ?? {};
      const existing = employeeMap?.[employeeId] ?? {};

      const candidate = 
      onlyEarnedForThePeriod
        ? { earnedForThePeriod: keyItem.earnedForThePeriod }
        :
         {
            // ...CLEARED_EMPLOYEE_TAX_FIELDS(),

            ...(payrollType === "Augmentation Pay" && { apTax: keyItem.apTax }),
            ...(payrollType === "Individual Payroll" && {
              individualTax: keyItem.individualTax,
            }),

            ...((isnotJoBasic || isIndividual) && {
              oldRate: keyItem.oldRate,
              mandatoryDeductions: keyItem.mandatoryDeductions,
              daysInAmonth: keyItem.daysInAmonth,
              gsisInsuredDays: keyItem.gsisInsuredDays,
            }),
            ...(isAddendumBasic && {
              earnedBasic: keyItem.earnedBasic,
              daysToBeDeducted: keyItem.daysToBeDeducted,
              halfDaysToBeDeducted: keyItem.halfDaysToBeDeducted,
              minutesToBeDeducted: keyItem.minutesToBeDeducted,
            }),
          };

      const changed = { id: employeeId };
      let hasChange = false;

      for (const [field, value] of Object.entries(candidate)) {
        if (value == null) continue;
        if (Array.isArray(value) && _areArraysEqual(value, existing[field])) {
          continue;
        }
        changed[field] = value;
        hasChange = true;
      }

      if (hasChange) updates.push(changed);
    }

    return updates;
  }

  _isJoEarnedOnlyUpdate(objectData) {
    const checkCase = this.withItem(objectData);
    return checkCase("isBasicJoSalary") || checkCase("isBasicJoAddendum");
  }

  _showPrintProgress({
    totalCount,
    title = "Updating Payroll Status",
    processingMessage = "Updating payroll status...",
    successMessage = "Payroll marked as printed!",
  }) {
    this.view.setState({
      showProgressModal: true,
      saveProgress: 0,
      savedCount: 0,
      totalCount,
      progressTitle: title,
      progressMessage: processingMessage,
      progressSuccessMessage: successMessage,
    });
  }

  _hidePrintProgress() {
    this.view.setState({
      showProgressModal: false,
      saveProgress: 0,
      savedCount: 0,
      totalCount: 0,
    });
  }

  async _updatePayrollListStatus(objectData, dvNumber) {
    await this.upsertUseCase.execute("payroll_list", {
      id: objectData?.id,
      dvNumber,
      stats: "Printed",
    });
  }

  async _completePrintProgress(totalCount) {
    const count = totalCount ?? this.view.state.totalCount ?? 1;

    this.view.setState({
      saveProgress: 100,
      savedCount: count,
    });

    await delay(500);
    this._hidePrintProgress();
  }

  async _finishPrintFlow(objectData, dvNumber, totalCount) {
    await this._updatePayrollListStatus(objectData, dvNumber);
    await this._completePrintProgress(totalCount);
    await this.view.refetchData();
    // this.view.showSuccess("Status Change to Printed");
  }

  async _bulkUpdateEmployees(employeeUpdates, onProgress) {
    let pending = employeeUpdates;
    let savedBase = 0;
    let lastError;

    for (const concurrency of EMPLOYEE_PRINT_CONCURRENCY) {
      try {
        await bulkUpsertUseCase.executeMany("employees", pending, {
          concurrency,
          progressInterval: 50,
          continueOnItemError: true,
          skipNormalize: true,
          onProgress: (completed, _total, meta = {}) => {
            const failedCount = meta.failedCount ?? 0;
            onProgress(
              savedBase + completed - failedCount,
              employeeUpdates.length
            );
          },
        });
        return;
      } catch (error) {
        lastError = error;
        if (!isRetryableError(error)) throw error;

        const failedIndexes = error.failedIndexes;
        if (failedIndexes?.length) {
          savedBase += pending.length - failedIndexes.length;
          pending = failedIndexes.map((index) => pending[index]);
        }
        await delay(800);
      }
    }

    throw lastError;
  }

  async _handlePrintData(data) {
    const objectData = data.data;

    const isBackpay = this._isBackPay(objectData);
    const { payrollType, payItem, jobType } = objectData;
    const dvNumber =
      this.view.state.panelState?.dvNumber ?? objectData?.dvNumber ?? "";

    const isnotJoBasic = payrollType === "Basic Pay" && jobType !== "Job Order";
    const isIndividual =
      payrollType === "Individual Payroll" &&
      (payItem === "Initial Salary" || payItem === "Last Salary");

    const isAddendumBasic =
      payrollType === "Basic Pay" &&
      jobType !== "Job Order" &&
      payItem === "Addendum Payroll";

    // const skipEmployeeUpdate = this._shouldSkipEmployeeUpdate(objectData);
    const onlyEarnedForThePeriod = this._isJoEarnedOnlyUpdate(objectData);

    try {
      dialog.close();

      // if (skipEmployeeUpdate) {
      //   this._showPrintProgress({
      //     totalCount: 1,
      //     title: "Updating Payroll",
      //     processingMessage: "Updating payroll status...",
      //     successMessage: "Payroll marked as printed!",
      //   });

      //   await this._finishPrintFlow(objectData, dvNumber, 1);
      //   return;
      // }

      const employees = data?.employees;

      const employeeMap = _buildEmployeeMap(employees, keys);
      const concattedMonthYear = _formatDateToMonthYear(objectData?.startDate);
      const items = _getProcessItems({
        data: data?.payrollData,
        objectData,
        isBackpay,
        concattedMonthYear,
        employeeMap,
      });

      const employeeUpdates = this._buildEmployeeUpdates({
        payrollData: data?.payrollData,
        items,
        employeeMap,
        payrollType,
        isnotJoBasic,
        isIndividual,
        isAddendumBasic,
        onlyEarnedForThePeriod,
      });

      // console.log("employeeUpdates", employeeUpdates)

      const totalRecords = employeeUpdates.length;

      if (totalRecords === 0) {
        this._showPrintProgress({
          totalCount: 1,
          processingMessage: "No employee tax changes — updating status...",
        });
        await this._finishPrintFlow(objectData, dvNumber, 1);
        return;
      }

      this._showPrintProgress({ totalCount: totalRecords });

      await this._bulkUpdateEmployees(employeeUpdates, (savedCount) => {
        this.view.setState({
          saveProgress: Math.round((savedCount / totalRecords) * 100),
          savedCount,
        });
      });

      await this._finishPrintFlow(objectData, dvNumber, totalRecords);
    } catch (error) {
      console.log(error, "Something went wrong");
      this._hidePrintProgress();
      const message = isRetryableError(error)
        ? "Connection overloaded while updating employee records. Please try again — fewer records will save at a time automatically."
        : "Failed to update payroll status. Please try again.";
      this.view.showError(message);
    }
  }
}
export default PayrollListlPresenter;
